// src/main/content/contests/ContestFormBuilder.js
//
// Single-step create / edit form for a contest.
//
// Fields surfaced:
//   - name, description
//   - locations (multi-select; auto-hidden when user only sees one store)
//   - participants (multi-select; empty = "everyone at the selected stores")
//   - metric_key (dropdown)
//   - start_date, end_date
//   - winners_count
//   - min_shifts_to_rank
//   - reward_description
//
// Locations + employees follow the same pattern as
// `EmployeeReportingDisplay`: store multiselect, then per-store parallel
// fetch of STMs via `API_get_store_team_members`, then employee multiselect
// scoped to the loaded set. Empty participants is the default and is sent
// to the backend as an empty array, which the backend interprets as "all
// non-archived STMs at the selected stores." Selecting Everyone explicitly
// is also collapsed to an empty array so a new hire who joins mid-contest
// automatically appears on the leaderboard.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import AiAssetGeneratorModal from "../create_training_content/AiAssetGeneratorModal";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";

import {
  getDefaultAxiosConfig,
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  API_create_contest,
  API_delete_contest,
  API_get_available_metrics,
  API_get_eligible_stores,
  API_get_store_team_members,
  API_update_contest,
} from "../../../constants";
import { formatName } from "../../../helpers/helpers";
import {
  canBuildRatio,
  composeRatioKey,
  parseRatioKey,
  ratioBuildingBlocks,
  withSavedSalesGroup,
} from "./contestRatioKeys";

function formatDateReadable(date) {
  if (!date) return "";
  return dayjs(date).format("ddd, MMM DD, YYYY");
}

function formatHint(meta) {
  if (!meta) return "";
  const parts = [];
  if (meta.format === "dollars") parts.push("Tracked in dollars");
  else if (meta.format === "hours") parts.push("Tracked in hours");
  else if (meta.format === "percentage") parts.push("Tracked as a percentage");
  else if (meta.format === "points_delta") parts.push("Tracked as points vs target");
  else if (meta.format === "goal_percent") parts.push("Tracked as % of each location's goal");
  else if (meta.format === "number") parts.push("Tracked as a count");
  else if (meta.format === "rate") parts.push("Tracked as a rate");
  if (meta.direction === "higher_is_better") parts.push("higher is better");
  else if (meta.direction === "lower_is_better") parts.push("lower is better");
  else if (meta.direction === "closest_to_100") parts.push("closest to 100% wins");
  return parts.join(" · ");
}

// ---------- Ratio metric composition ----------

// Sentinel dropdown value for the "build a ratio" pseudo-metric. Never sent
// to the backend — picking it just opens the numerator/denominator selects.
const RATIO_OPTION_VALUE = "__ratio__";
const RATIO_OPTION = {
  value: RATIO_OPTION_VALUE,
  label: "Ratio of two metrics",
  format: "rate",
  direction: "higher_is_better",
};

// Shared sx for the MUI Select fields so they sit visually flush with the
// custom-styled TextFields elsewhere on the form.
const SELECT_SX = {
  backgroundColor: "white",
  borderRadius: "10px",
  height: 45,
  fontSize: 14,
  width: "100%",
  // Same shared gray as the TextFields / custom dropdowns (see textFieldSx).
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
  "&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
    borderColor: "#0f172a",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6d28d9" },
  "& .MuiSelect-select": {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const CHECKBOX_SX = {
  color: "#6d28d9",
  "&.Mui-checked": { color: "#6d28d9" },
  "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
};

// ---------- Date field ----------

// Named export: ContestDuplicateModal reuses this for the "run again"
// date range so the two surfaces stay visually identical.
export function DateField({ label, value, onChange, minDate }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const dayjsValue = value ? dayjs(value) : null;

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="flex-1">
      <p className="text-sm font-medium text-slate-700 mb-1.5">{label}</p>
      <div
        ref={ref}
        className={`bg-white rounded-lg px-4 py-3 text-sm relative ${
          open
            ? "border-2 border-violet-700"
            : "border border-slate-300 hover:border-slate-900"
        }`}
      >
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setOpen(!open)}
        >
          {dayjsValue ? (
            <p className="text-slate-900">{formatDateReadable(dayjsValue)}</p>
          ) : (
            <p className="text-slate-400">Select date</p>
          )}
          <CalendarTodayIcon className="text-slate-500" style={{ fontSize: 16 }} />
        </div>
        {open && (
          <div className="absolute top-12 left-0 bg-white z-20 border border-slate-200 rounded-xl shadow-xl overflow-hidden">
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateCalendar
                value={dayjsValue}
                minDate={minDate ? dayjs(minDate) : undefined}
                onChange={(d) => {
                  onChange(d.format("YYYY-MM-DD"));
                  setOpen(false);
                }}
                slotProps={{
                  day: {
                    sx: {
                      "&.MuiPickersDay-root.Mui-selected": {
                        backgroundColor: "#6d28d9",
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------- Metric select ----------

function MetricSelect({ value, onChange, metrics }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const selected = metrics.find((m) => m.value === value);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div
        onClick={() => setOpen(!open)}
        className={`w-full h-11 px-4 py-2 bg-white border rounded-lg cursor-pointer flex items-center justify-between transition-colors ${
          open
            ? "border-violet-700 border-2"
            : "border-slate-300 hover:border-slate-900"
        }`}
      >
        <span className={selected ? "text-slate-900" : "text-slate-400"}>
          {selected ? selected.label : "Select a metric"}
        </span>
        <KeyboardArrowDownIcon
          className={`text-slate-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
          fontSize="small"
        />
      </div>
      {open && (
        <div className="absolute top-12 left-0 right-0 bg-white border border-slate-200 rounded-lg shadow-lg z-50 max-h-72 overflow-y-auto">
          {metrics.length === 0 ? (
            <div className="px-4 py-3 text-slate-400 italic text-sm">
              No metrics available
            </div>
          ) : (
            metrics.map((m) => (
              <div
                key={m.value}
                onClick={() => {
                  onChange(m.value);
                  setOpen(false);
                }}
                className="px-4 py-2.5 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0"
              >
                <div className="text-sm text-slate-900">{m.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {formatHint(m)}
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Contest type toggle ----------
//
// Segmented two-option control (Individual / Store vs Store). Disabled in
// edit mode because contest type is immutable post-creation — the backend
// would 400 a change anyway, but the explicit disabled state is friendlier
// than letting the user try and fail.

const CONTEST_TYPE_OPTIONS = [
  {
    value: "individual",
    label: "Individual",
    description: "Rank employees across one or more locations.",
  },
  {
    value: "store_vs_store",
    label: "Store vs Store",
    description: "Rank locations against each other.",
  },
];

function ContestTypeToggle({ value, onChange, disabled }) {
  return (
    <div
      className={`grid grid-cols-2 gap-2 ${disabled ? "opacity-60" : ""}`}
      role="radiogroup"
    >
      {CONTEST_TYPE_OPTIONS.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            disabled={disabled}
            onClick={() => !disabled && onChange(opt.value)}
            className={`text-left px-4 py-3 rounded-lg border-2 transition-colors ${
              selected
                ? "border-violet-700 bg-violet-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
          >
            <div
              className={`text-sm font-semibold ${
                selected ? "text-violet-700" : "text-slate-900"
              }`}
            >
              {opt.label}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
          </button>
        );
      })}
    </div>
  );
}

// ---------- Visibility toggle ----------
//
// Segmented two-option control for who can view the contest. "Everyone" is the
// default (anyone at a participating store); "Managers only" hides it from the
// employee tier — for manager-facing competitions you don't want line staff
// watching.

// Winner rule for per-store-target metrics (Labor % vs Target, sales-driver
// ratio vs target). Only rendered when the picked metric requires targets;
// every other metric is implicitly "top_n" and never sends the field.
const WINNER_MODE_OPTIONS = [
  {
    value: "top_n",
    label: "Top finishers",
    description: "A fixed number of top-ranked locations win.",
  },
  {
    value: "hit_target",
    label: "Everyone who hits target",
    description:
      "Every location that meets or beats its own target wins, however many that is.",
  },
];

const VISIBILITY_OPTIONS = [
  {
    value: "everyone",
    label: "Everyone",
    description: "Anyone at a participating location can view it.",
  },
  {
    value: "managers",
    label: "Managers only",
    description: "Hidden from employees; only managers and admins can view it.",
  },
];

function VisibilityToggle({ value, onChange }) {
  return <CardRadioGroup options={VISIBILITY_OPTIONS} value={value} onChange={onChange} />;
}

// Two-up card radio used by the visibility + winner-rule pickers.
function CardRadioGroup({ options, value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2" role="radiogroup">
      {options.map((opt) => {
        const selected = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={selected}
            onClick={() => onChange(opt.value)}
            className={`text-left px-4 py-3 rounded-lg border-2 transition-colors cursor-pointer ${
              selected
                ? "border-violet-700 bg-violet-50"
                : "border-slate-200 bg-white hover:border-slate-300"
            }`}
          >
            <div
              className={`text-sm font-semibold ${
                selected ? "text-violet-700" : "text-slate-900"
              }`}
            >
              {opt.label}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">{opt.description}</div>
          </button>
        );
      })}
    </div>
  );
}

// ---------- Locations multiselect ----------
//
// Only rendered when the user sees more than one store. With a single store
// the picker is hidden and the form silently scopes to that store (cleaner
// UX for store managers who only see their own location).

function LocationsMultiSelect({ storeNames, selectedStoreIds, onChange }) {
  // String IDs internally so MUI <Select multiple> + checkboxes compare
  // equality consistently across re-renders. We convert back to ints on
  // submit.
  const selectedSet = useMemo(() => new Set(selectedStoreIds), [selectedStoreIds]);
  const allIds = storeNames.map((s) => String(s.id));

  const handleChange = (event) => {
    const raw = event.target.value;
    const next = Array.isArray(raw) ? raw : [raw];
    if (next.includes("__all__")) {
      // Toggle select-all / deselect-all.
      if (selectedStoreIds.length === storeNames.length) {
        onChange([]);
      } else {
        onChange(allIds);
      }
      return;
    }
    onChange(next.map(String).filter((v) => v !== ""));
  };

  return (
    <Select
      multiple
      value={selectedStoreIds}
      onChange={handleChange}
      input={<OutlinedInput />}
      sx={SELECT_SX}
      MenuProps={{ PaperProps: { style: { maxHeight: 500, minWidth: 280 } } }}
      renderValue={(selected) => {
        const names = storeNames
          .filter((s) => selectedSet.has(String(s.id)))
          .map((s) => s.store_name);
        if (names.length === 0) return "Select locations…";
        if (names.length <= 2) return names.join(", ");
        return `${names[0]}, ${names[1]}, +${names.length - 2}`;
      }}
    >
      <MenuItem value="__all__">
        <Checkbox
          checked={
            storeNames.length > 0 &&
            selectedStoreIds.length === storeNames.length
          }
          indeterminate={
            selectedStoreIds.length > 0 &&
            selectedStoreIds.length < storeNames.length
          }
          sx={CHECKBOX_SX}
        />
        <ListItemText
          primary={
            selectedStoreIds.length === storeNames.length
              ? "Deselect all"
              : "Select all"
          }
          primaryTypographyProps={{ style: { fontWeight: 600 } }}
        />
      </MenuItem>
      {storeNames.map((s) => (
        <MenuItem key={s.id} value={String(s.id)}>
          <Checkbox checked={selectedSet.has(String(s.id))} sx={CHECKBOX_SX} />
          <ListItemText primary={s.store_name} />
        </MenuItem>
      ))}
    </Select>
  );
}

// ---------- Employees multiselect ----------
//
// Operates on the `employees` array passed in (already loaded for the
// currently-selected stores). Each entry is an STM record + a store_name
// tag we added at fetch time. When no employees are loaded (because no
// stores are selected) we render a disabled placeholder so the layout
// doesn't jump.

function EmployeesMultiSelect({
  employees,
  selectedStmIds,
  onChange,
  loading,
  showStoreTags,
}) {
  const [search, setSearch] = useState("");
  const selectedSet = useMemo(
    () => new Set(selectedStmIds.map(String)),
    [selectedStmIds]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return employees;
    const q = search.trim().toLowerCase();
    return employees.filter((e) => {
      const full = `${e.first_name || ""} ${e.last_name || ""}`.toLowerCase();
      const sn = (e.store_name || "").toLowerCase();
      return full.includes(q) || sn.includes(q);
    });
  }, [employees, search]);

  const handleChange = (event) => {
    const raw = event.target.value;
    const next = Array.isArray(raw) ? raw : [raw];
    if (next.includes("__all__")) {
      if (selectedStmIds.length === employees.length) {
        onChange([]);
      } else {
        onChange(employees.map((e) => String(e.id)));
      }
      return;
    }
    onChange(next.map(String).filter((v) => v !== ""));
  };

  // Disabled state when there are no employees loaded yet.
  if (loading || employees.length === 0) {
    return (
      <div className="h-[45px] px-4 flex items-center bg-slate-50 rounded-lg border border-slate-200 text-sm text-slate-400">
        {loading ? "Loading employees…" : "Pick a location first"}
      </div>
    );
  }

  return (
    <Select
      multiple
      value={selectedStmIds.map(String)}
      onChange={handleChange}
      onClose={() => setSearch("")}
      input={<OutlinedInput />}
      sx={SELECT_SX}
      MenuProps={{
        autoFocus: false,
        PaperProps: { style: { maxHeight: 600, minWidth: 320 } },
      }}
      renderValue={(selected) => {
        if (selected.length === 0) {
          return "Everyone at selected locations";
        }
        if (selected.length === employees.length) {
          return `Everyone at selected locations (${employees.length})`;
        }
        const names = employees
          .filter((e) => selectedSet.has(String(e.id)))
          .map((e) => {
            const n = `${formatName(e.first_name)} ${formatName(e.last_name)}`;
            return showStoreTags ? `${n} (${e.store_name})` : n;
          });
        if (names.length <= 2) return names.join(", ");
        return `${names[0]}, ${names[1]}, +${names.length - 2}`;
      }}
    >
      <MenuItem
        dense
        disableRipple
        onKeyDown={(e) => e.stopPropagation()}
        // MUI applies background colors via class selectors that win over
        // a bare ``backgroundColor`` rule on the MenuItem — so when the
        // first employee row scrolls behind this sticky header, MUI's
        // focus/hover/selected backgrounds bleed through and the search
        // input looks transparent. Targeting the MUI root + state selectors
        // explicitly forces an opaque white over every state combination,
        // and the higher z-index keeps focused list items below.
        sx={{
          "&.MuiMenuItem-root, &.MuiMenuItem-root:hover, &.MuiMenuItem-root.Mui-focusVisible, &.MuiMenuItem-root.Mui-selected, &.MuiMenuItem-root.Mui-selected:hover": {
            backgroundColor: "#ffffff",
          },
          cursor: "default",
          position: "sticky",
          top: 0,
          zIndex: 100,
          padding: "8px 16px",
          borderBottom: "1px solid #e2e8f0",
          marginBottom: "4px",
        }}
      >
        <TextField
          size="small"
          autoFocus
          placeholder="Search employees…"
          fullWidth
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          InputProps={{
            sx: {
              borderRadius: "8px",
              fontSize: 14,
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#6d28d9",
              },
            },
          }}
        />
      </MenuItem>
      <MenuItem value="__all__">
        <Checkbox
          checked={
            employees.length > 0 && selectedStmIds.length === employees.length
          }
          indeterminate={
            selectedStmIds.length > 0 &&
            selectedStmIds.length < employees.length
          }
          sx={CHECKBOX_SX}
        />
        <ListItemText
          primary={
            selectedStmIds.length === employees.length
              ? "Deselect all"
              : "Select all"
          }
          primaryTypographyProps={{ style: { fontWeight: 600 } }}
        />
      </MenuItem>
      {filtered.map((e) => {
        const name = `${formatName(e.first_name)} ${formatName(e.last_name)}`;
        const displayName = showStoreTags ? `${name} (${e.store_name})` : name;
        return (
          <MenuItem key={e.id} value={String(e.id)}>
            <Checkbox
              checked={selectedSet.has(String(e.id))}
              sx={CHECKBOX_SX}
            />
            <ListItemText primary={displayName} />
          </MenuItem>
        );
      })}
    </Select>
  );
}

// ---------- main ----------

export default function ContestFormBuilder({
  contest,
  homeStore,
  storeNames,
  stmId,
  onClose,
}) {
  const isEdit = Boolean(contest);

  const [catalogMetrics, setMetrics] = useState([]);
  const metrics = useMemo(
    () => withSavedSalesGroup(catalogMetrics, contest),
    [catalogMetrics, contest]
  );
  // String IDs for the dropdowns; we convert back to ints when sending.
  const initialStoreIds = useMemo(() => {
    if (contest?.store_ids?.length) {
      return contest.store_ids.map(String);
    }
    return homeStore != null ? [String(homeStore)] : [];
  }, [contest, homeStore]);
  const initialParticipantIds = useMemo(() => {
    if (contest?.participant_stm_ids?.length) {
      return contest.participant_stm_ids.map(String);
    }
    return [];
  }, [contest]);

  // contest_type is immutable after creation (the backend rejects changes
  // because participants/metric/leaderboard rows are all different shapes
  // between the two flavors). For new contests, default to "individual".
  const initialContestType = contest?.contest_type || "individual";
  const [contestType, setContestType] = useState(initialContestType);
  const [aiGenOpen, setAiGenOpen] = useState(false);

  const [form, setForm] = useState({
    name: contest?.name || "",
    description: contest?.description || "",
    metric_key: contest?.metric_key || "",
    start_date: contest?.start_date || "",
    end_date: contest?.end_date || "",
    winner_mode: contest?.winner_mode || "top_n",
    winners_count: contest?.winners_count ?? 1,
    min_shifts_to_rank: contest?.min_shifts_to_rank ?? 0,
    min_denominator_to_rank: contest?.min_denominator_to_rank ?? 0,
    reward_description: contest?.reward_description || "",
    // Drafts force-store 'managers' server-side (deploy-window safety), so
    // the stored value isn't the creator's intent — seed the picker with the
    // default instead; it applies at publish time.
    visibility: contest?.is_draft
      ? "everyone"
      : contest?.visibility || "everyone",
  });
  // Ratio-metric composer state. Prefilled from the saved key in edit mode;
  // `ratioNum`/`ratioDen` hold plain dropdown values and the composed key
  // (see contestRatioKeys.js) lives in form.metric_key like any other metric.
  const initialRatioPicks = useMemo(
    () => parseRatioKey(contest?.metric_key),
    [contest]
  );
  const [ratioMode, setRatioMode] = useState(Boolean(initialRatioPicks));
  const [ratioNum, setRatioNum] = useState(initialRatioPicks?.num || "");
  const [ratioDen, setRatioDen] = useState(initialRatioPicks?.den || "");

  const [selectedStoreIds, setSelectedStoreIds] = useState(initialStoreIds);
  const [employees, setEmployees] = useState([]);
  const [selectedStmIds, setSelectedStmIds] = useState(initialParticipantIds);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // "Limit to specific employees" toggle. Default OFF (= open to
  // everyone at the selected locations). Open in edit mode IFF the
  // saved contest has an explicit participants list — otherwise the
  // toggle's default ON would falsely promise the user is editing a
  // pick list when they aren't.
  const [limitToSpecific, setLimitToSpecific] = useState(
    initialParticipantIds.length > 0
  );

  // Per-store targets for metrics that rank against a per-store goal (e.g.
  // Labor % vs Target). Keyed by store-id STRING → { target_value, gm_label }.
  // Values are kept as strings for controlled inputs; coerced to numbers on
  // submit. Prefilled from the saved contest in edit mode.
  const initialStoreTargets = useMemo(() => {
    const m = {};
    (contest?.store_targets || []).forEach((t) => {
      m[String(t.store_id)] = {
        target_value: t.target_value != null ? String(t.target_value) : "",
        gm_label: t.gm_label || "",
      };
    });
    return m;
  }, [contest]);
  const [storeTargets, setStoreTargets] = useState(initialStoreTargets);

  const updateStoreTarget = (storeId, field, value) => {
    setStoreTargets((prev) => ({
      ...prev,
      [storeId]: {
        ...(prev[storeId] || { target_value: "", gm_label: "" }),
        [field]: value,
      },
    }));
  };

  // Cache the initial join string so the "clear participants on store
  // change" effect can compare current-vs-initial instead of using a
  // first-mount ref. The ref pattern double-fires under React StrictMode
  // (dev mount-unmount-remount) and clears picks on the second mount;
  // string comparison is invariant to extra effect runs.
  const initialStoreIdsKey = useMemo(
    () => initialStoreIds.join(","),
    [initialStoreIds]
  );
  const selectedStoreIdsKey = selectedStoreIds.join(",");

  // Eligible stores for the Locations dropdown. The `storeNames` prop
  // is the user's MEMBERSHIP-only store list — fine for managers, but
  // it excludes branches a HQ-based company_admin doesn't have an STM
  // at even though the create gate already permits them. Fetch the
  // contest-info app's eligible-stores list on mount so admins see
  // the full company roster, while managers keep the membership-only
  // scope. The `storeNames` prop is kept as a fallback for the loading
  // state and the storeInfoLookup label map.
  const [eligibleStores, setEligibleStores] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await getRightworkAPI(API_get_eligible_stores, {
          store_id: homeStore,
        });
        if (cancelled) return;
        setEligibleStores(res.data?.redux?.payload?.stores || []);
      } catch (err) {
        if (!cancelled) {
          // Fall back to the membership-only list silently — better
          // than blocking the form on a failed lookup.
          // eslint-disable-next-line no-console
          console.error("Failed to load eligible stores", err);
          setEligibleStores(null);
        }
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeStore]);

  // The list the dropdown actually renders. Distinguishes three
  // states:
  //   * `eligibleStores === null` → fetch in flight OR failed. Fall
  //     back to `storeNames` so the form is still usable while we wait
  //     / surface the failure mode.
  //   * `eligibleStores === []`   → fetch SUCCEEDED but the user has
  //     no eligible stores at this company. Show empty — falling back
  //     to `storeNames` here was the bug behind the "showing all
  //     companies" report: `storeNames` is the cross-company
  //     member-stores list, so the dropdown would silently leak
  //     stores from companies the user isn't authorized to create
  //     contests at.
  //   * `eligibleStores === [..]` → show the fetch result.
  const locationsSource = useMemo(() => {
    if (eligibleStores !== null) return eligibleStores;
    return storeNames || [];
  }, [eligibleStores, storeNames]);

  // Storefront map for fetchEmployees → store_name tagging. Built from
  // the broader `locationsSource` so a picked admin-only store still
  // resolves a name for the employee tag.
  const storeInfoLookup = useMemo(() => {
    const m = new Map();
    locationsSource.forEach((s) => m.set(String(s.id), s.store_name));
    return m;
  }, [locationsSource]);

  const showMultiStorePicker = locationsSource.length > 1;
  const showStoreTags = selectedStoreIds.length > 1;

  useEffect(() => {
    fetchMetrics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homeStore, contestType]);

  // When the user toggles contest type (only possible on a new contest —
  // edit mode disables the toggle), the metric registry changes. Clear
  // the selected metric, picked participants, AND the
  // limit-to-specific toggle. Without the toggle reset, going
  // Individual→SVS→Individual leaves the toggle ON with an empty
  // selection, and the submit silently sends `participant_stm_ids=[]`
  // (= everyone) — contradicting the toggle's "only selected will
  // compete" framing.
  useEffect(() => {
    if (isEdit) return;
    setForm((f) => ({ ...f, metric_key: "" }));
    setSelectedStmIds([]);
    setLimitToSpecific(false);
    // Ratio picks use different driver-key prefixes per contest type
    // (sales_driver: vs store_sales_driver:), so picks from the previous
    // mode are invalid in the new one — close the composer and clear them.
    setRatioMode(false);
    setRatioNum("");
    setRatioDen("");
  }, [contestType, isEdit]);

  useEffect(() => {
    // Skip the per-store employee fetch entirely for store-vs-store
    // contests — the participants picker is hidden, so we'd just be
    // burning N parallel network requests for data we never render.
    if (contestType === "store_vs_store") {
      setEmployees([]);
      return;
    }
    fetchEmployees();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStoreIdsKey, contestType]);

  // Clear the picked-participants set whenever the user diverges from the
  // initial store selection. Comparing keys (rather than a first-mount ref)
  // means an edit form opens with saved picks intact even under React
  // StrictMode, which mounts-unmounts-remounts in dev and would otherwise
  // re-fire the clear effect.
  useEffect(() => {
    if (selectedStoreIdsKey === initialStoreIdsKey) return;
    setSelectedStmIds([]);
  }, [selectedStoreIdsKey, initialStoreIdsKey]);

  const fetchMetrics = async () => {
    try {
      const res = await getRightworkAPI(API_get_available_metrics, {
        store_id: homeStore,
        contest_type: contestType,
      });
      setMetrics(res.data?.redux?.payload?.metrics || []);
    } catch (err) {
      toast.error("Failed to load metric list", { autoClose: 3000 });
    }
  };

  const fetchEmployees = async () => {
    if (selectedStoreIds.length === 0) {
      setEmployees([]);
      return;
    }
    setEmployeesLoading(true);
    try {
      // ``getDefaultAxiosConfig`` is async — assigning its Promise to a
      // ``config`` variable and passing it to axios as-is would drop the
      // Authorization header and trigger a 401, leaving the participants
      // picker silently empty. Await once before kicking off the fan-out.
      const axiosConfig = await getDefaultAxiosConfig();
      const promises = selectedStoreIds.map((sid) =>
        axios.get(API_get_store_team_members + sid, axiosConfig)
      );
      const results = await Promise.all(promises);
      const all = [];
      results.forEach((res, idx) => {
        const sid = selectedStoreIds[idx];
        const storeName = storeInfoLookup.get(sid) || `Store ${sid}`;
        (res.data || [])
          .filter((emp) => !emp.is_hidden && !emp.is_archived)
          .forEach((emp) => {
            all.push({ ...emp, store_id: sid, store_name: storeName });
          });
      });
      setEmployees(all);
    } catch (err) {
      console.error("Error fetching employees for contest form", err);
      setEmployees([]);
    } finally {
      setEmployeesLoading(false);
    }
  };

  const handleChange = (updates) => setForm((f) => ({ ...f, ...updates }));

  // Seed the form from an AI draft. Stores and participants stay whatever
  // the author picked (default: the current store); a generated metric is
  // always a plain registry key, so the ratio composer switches off to let
  // the dropdown show it.
  const applyAiDraft = (draft) => {
    setForm((prev) => ({
      ...prev,
      name: draft.name || prev.name,
      description: draft.description || "",
      metric_key: draft.metric_key || prev.metric_key,
      start_date: draft.start_date || prev.start_date,
      end_date: draft.end_date || prev.end_date,
      winners_count: draft.winners_count ?? prev.winners_count,
      min_shifts_to_rank: draft.min_shifts_to_rank ?? prev.min_shifts_to_rank,
      reward_description: draft.reward_description || "",
      visibility: draft.visibility || prev.visibility,
    }));
    if (draft.metric_key) {
      setRatioMode(false);
      setRatioNum("");
      setRatioDen("");
    }
  };

  const isStoreVsStore = contestType === "store_vs_store";

  // The plain metrics a ratio can divide. The pseudo-option only appears when
  // there's something to combine.
  const { numerators: ratioNumerators, denominators: ratioDenominators } = useMemo(
    () => ratioBuildingBlocks(metrics, isStoreVsStore),
    [metrics, isStoreVsStore]
  );
  const showRatioOption = canBuildRatio({
    numerators: ratioNumerators,
    denominators: ratioDenominators,
  });
  const metricOptions = useMemo(
    () => (showRatioOption ? [...metrics, RATIO_OPTION] : metrics),
    [metrics, showRatioOption]
  );

  // Update one side of the ratio and (re)compose form.metric_key. The key
  // stays empty until both sides are picked so the generic "Pick a metric"
  // validation keeps working.
  const handleRatioPick = (side, v) => {
    const num = side === "num" ? v : ratioNum;
    const den = side === "den" ? v : ratioDen;
    if (side === "num") setRatioNum(v);
    else setRatioDen(v);
    handleChange({ metric_key: composeRatioKey(num, den, isStoreVsStore) });
  };

  // For ratio mode, synthesize the descriptor the hint/targets logic expects
  // (metrics.find can't match a composed key — the list only has the
  // building blocks). Null while either side is unpicked, or when editing a
  // contest whose underlying driver has since been deleted; the hint just
  // hides in that case.
  const ratioNumMeta = metrics.find((m) => m.value === ratioNum);
  const ratioDenMeta = metrics.find((m) => m.value === ratioDen);
  const selectedMetric = ratioMode
    ? ratioNumMeta && ratioDenMeta
      ? isStoreVsStore
        ? {
            // Store flavor ranks each store on its ratio percent minus that
            // store's own target, so it's a points delta and needs the
            // per-store target inputs — setting requires_store_targets here
            // is what flips the whole target section + payload on.
            value: form.metric_key,
            label: `${ratioNumMeta.label} per ${ratioDenMeta.label} vs Target`,
            format: "points_delta",
            direction: "higher_is_better",
            requires_store_targets: true,
          }
        : {
            value: form.metric_key,
            label: `${ratioNumMeta.label} per ${ratioDenMeta.label}`,
            format: "rate",
            direction: "higher_is_better",
          }
      : null
    : metrics.find((m) => m.value === form.metric_key);
  // Metrics that rank stores against a per-store target require a target for
  // every selected location (only store-vs-store metrics can set this).
  // While the metric catalog is still loading (or failed to load) in edit
  // mode, fall back to the serialized contest's own flag so long as the key
  // hasn't been changed — otherwise a save of an unrelated field would see
  // requiresTargets=false and silently downgrade a hit_target contest to
  // top_n (and drop its per-store targets from the payload).
  const requiresTargets = selectedMetric
    ? Boolean(selectedMetric.requires_store_targets)
    : Boolean(
        contest?.requires_store_targets && form.metric_key === contest?.metric_key
      );
  // Hit-target winners only exist for targeted metrics; if the metric changes
  // to a non-targeted one the form silently reverts to top_n (the backend
  // normalizes the same way).
  const hitTargetMode = requiresTargets && form.winner_mode === "hit_target";
  // Goal metrics ("<driver> vs Goal") take each location's goal total rather
  // than a percent target; only the target section's wording changes.
  const isGoalMetric = selectedMetric?.format === "goal_percent";

  const validate = () => {
    if (!form.name.trim()) return "Name is required";
    if (selectedStoreIds.length === 0) return "Pick at least one location";
    if (isStoreVsStore && selectedStoreIds.length < 2) {
      return "Store-vs-store contests need at least 2 locations";
    }
    if (ratioMode) {
      if (!ratioNum || !ratioDen) return "Pick both metrics for the ratio";
      if (ratioNum === ratioDen)
        return "Numerator and denominator must be different metrics";
    }
    if (!form.metric_key) return "Pick a metric";
    if (!form.start_date || !form.end_date) return "Start and end dates are required";
    if (dayjs(form.end_date).isBefore(dayjs(form.start_date)))
      return "End date can't be before start date";
    if (!hitTargetMode && Number(form.winners_count) < 1)
      return "Winners count must be at least 1";
    if (!isStoreVsStore && Number(form.min_shifts_to_rank) < 0)
      return "Min shifts to rank can't be negative";
    if (ratioMode && !isStoreVsStore && Number(form.min_denominator_to_rank) < 0)
      return "Min denominator to rank can't be negative";
    // Reject "toggle ON but no picks" — without this gate the submit
    // silently collapses to participant_stm_ids=[] (= everyone) and
    // contradicts the toggle's "only selected will compete" framing.
    if (!isStoreVsStore && limitToSpecific && selectedStmIds.length === 0) {
      return "Pick at least one employee, or turn off Limit to specific employees";
    }
    // Per-store targets: every selected location needs a positive number.
    if (requiresTargets) {
      for (const sid of selectedStoreIds) {
        const t = storeTargets[sid];
        const val = t ? Number(t.target_value) : NaN;
        if (!t || t.target_value === "" || Number.isNaN(val) || val <= 0) {
          return "Set a target greater than 0 for every location";
        }
      }
    }
    return null;
  };

  const handleSubmit = async ({ isDraft } = {}) => {
    const err = validate();
    if (err) {
      toast.error(err, { autoClose: 3000 });
      return;
    }
    setSubmitting(true);

    // Resolve the final participant_stm_ids to send. Three branches:
    //   1. Store-vs-store contests have no participant set.
    //   2. Toggle OFF (= open to everyone) — always send empty,
    //      regardless of any state leftover from a previous toggle ON.
    //   3. Toggle ON — send the picked subset, BUT collapse "selected
    //      everyone" to empty so a new hire who joins mid-contest
    //      automatically appears on the leaderboard.
    let participantIds;
    if (isStoreVsStore || !limitToSpecific) {
      participantIds = [];
    } else if (
      selectedStmIds.length === 0 ||
      (employees.length > 0 && selectedStmIds.length === employees.length)
    ) {
      participantIds = [];
    } else {
      participantIds = selectedStmIds.map((s) => Number(s));
    }

    const payload = {
      store_id: homeStore,
      name: form.name.trim(),
      description: form.description.trim(),
      contest_type: contestType,
      metric_key: form.metric_key,
      start_date: form.start_date,
      end_date: form.end_date,
      // Only targeted metrics carry a winner rule; everything else is top_n.
      // In hit-target mode winners_count is ignored by the backend but must
      // still be >= 1: keep the (hidden) value so toggling back to top-N
      // on a later edit restores it, clamped to 1 if it's blank/invalid.
      winner_mode: requiresTargets ? form.winner_mode : "top_n",
      winners_count: hitTargetMode
        ? Math.max(1, Number(form.winners_count) || 1)
        : Number(form.winners_count),
      // min_shifts_to_rank is a per-employee concept; force 0 for
      // store-vs-store so backend normalization isn't the only place this
      // gets cleaned up.
      min_shifts_to_rank: isStoreVsStore ? 0 : Number(form.min_shifts_to_rank),
      // Individual-ratio-only gate; send 0 for every other metric so a
      // metric change on edit can't leave a stale threshold behind. Store
      // ratio contests don't use it — a store's denominator volume is never
      // small enough to need a floor.
      min_denominator_to_rank:
        ratioMode && !isStoreVsStore
          ? Number(form.min_denominator_to_rank) || 0
          : 0,
      reward_description: form.reward_description.trim(),
      visibility: form.visibility,
      store_ids: selectedStoreIds.map((s) => Number(s)),
      participant_stm_ids: participantIds,
    };
    // Per-store targets ride along only for metrics that need them; the
    // backend requires one per selected store when the metric is targeted.
    if (requiresTargets) {
      payload.store_targets = selectedStoreIds.map((sid) => ({
        store_id: Number(sid),
        target_value: Number(storeTargets[sid].target_value),
        gm_label: (storeTargets[sid].gm_label || "").trim(),
      }));
    }
    if (isEdit) payload.contest_id = contest.id;

    // Draft flag. Create mode always states it explicitly; edit mode sends
    // it ONLY for contests that are currently drafts (save-draft vs
    // publish). Editing a live contest omits the key entirely — the backend
    // treats an absent key as "no change" and rejects live→draft anyway.
    if (!isEdit || contest?.is_draft) {
      payload.is_draft = !!isDraft;
    }

    try {
      const api = isEdit ? API_update_contest : API_create_contest;
      const res = await postRightworkAPI(api, payload);
      if (res.data?.redux?.payload?.status === "OK") {
        toast.success(
          isDraft
            ? "Draft saved"
            : contest?.is_draft
            ? "Contest published"
            : isEdit
            ? "Contest updated"
            : "Contest created",
          { autoClose: 2000 }
        );
        onClose(true);
      } else {
        throw new Error(res.data?.system?.message || "Server error");
      }
    } catch (e) {
      toast.error(e.message || "Save failed", { autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEdit) return;
    if (!window.confirm("Delete this contest? This can't be undone.")) return;
    setDeleting(true);
    try {
      const res = await postRightworkAPI(API_delete_contest, {
        contest_id: contest.id,
        store_id: homeStore,
      });
      if (res.data?.redux?.payload?.status === "OK") {
        toast.success("Contest deleted", { autoClose: 2000 });
        onClose(true);
      } else {
        throw new Error("Delete failed");
      }
    } catch (e) {
      toast.error(e.message || "Delete failed", { autoClose: 3000 });
    } finally {
      setDeleting(false);
    }
  };

  // Style shared by the text inputs.
  const textFieldSx = {
    backgroundColor: "white",
    borderRadius: "10px",
    // Match the custom dropdown / date-field borders so every input on the
    // form shares one gray: slate-300 at rest, slate-900 on hover, violet on
    // focus (MUI's default resting outline is a different gray — rgba(0,0,0,.23)).
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" },
    "&:hover:not(.Mui-focused) .MuiOutlinedInput-notchedOutline": {
      borderColor: "#0f172a",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#6d28d9",
    },
  };

  const participantSummary = (() => {
    if (employees.length === 0) return "";
    const isAll =
      selectedStmIds.length === 0 || selectedStmIds.length === employees.length;
    if (isAll) {
      return `All ${employees.length} employee${employees.length === 1 ? "" : "s"} at the selected location${
        selectedStoreIds.length === 1 ? "" : "s"
      }.`;
    }
    return `${selectedStmIds.length} of ${employees.length} employees selected.`;
  })();

  return (
    <div className="w-full px-6 mt-6 pb-20 max-w-3xl mx-auto">
      {/* Top action bar */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => onClose(false)}
          className="text-slate-500 hover:text-slate-700 text-sm flex items-center gap-1 px-2 py-1 -ml-2 rounded-lg"
        >
          <ChevronLeftIcon style={{ fontSize: 18 }} />
          Back
        </button>
        <div className="flex items-center gap-2">
          {isEdit && (
            <button
              onClick={handleDelete}
              disabled={deleting || submitting}
              className="px-4 py-2 rounded-lg bg-rose-50 hover:bg-rose-100 border border-rose-200 text-rose-700 text-sm font-medium disabled:opacity-60"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          )}
          <button
            onClick={() => onClose(false)}
            disabled={submitting || deleting}
            className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-sm font-medium disabled:opacity-60"
          >
            Cancel
          </button>
          {/* Draft controls. Create mode: secondary "Save as draft" beside
              the primary create. Editing a draft: secondary keeps it a
              draft, primary publishes. Editing a live contest: single
              "Save changes" that never sends is_draft (publishing is
              one-way; the backend rejects live→draft). */}
          {(!isEdit || contest?.is_draft) && (
            <button
              onClick={() => handleSubmit({ isDraft: true })}
              disabled={submitting || deleting}
              className="px-4 py-2 rounded-lg bg-amber-50 hover:bg-amber-100 border border-amber-200 text-amber-800 text-sm font-medium disabled:opacity-60"
            >
              {submitting ? "Saving…" : isEdit ? "Save draft" : "Save as draft"}
            </button>
          )}
          <button
            onClick={() => handleSubmit({ isDraft: false })}
            disabled={submitting || deleting}
            className="px-4 py-2 rounded-lg bg-violet-700 hover:bg-violet-800 text-white text-sm font-medium disabled:opacity-60"
          >
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : contest?.is_draft
              ? "Publish contest"
              : isEdit
              ? "Save changes"
              : "Create contest"}
          </button>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-7">
        <div className="flex items-center gap-2 mb-6">
          <EmojiEventsIcon className="text-violet-700" style={{ fontSize: 22 }} />
          <h2 className="text-xl font-semibold text-slate-900">
            {isEdit ? "Edit contest" : "New contest"}
          </h2>
          {!isEdit && (
            <button
              type="button"
              onClick={() => setAiGenOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-violet-700 border border-violet-200 hover:bg-violet-50 transition-colors"
            >
              <AutoAwesomeIcon style={{ fontSize: 16 }} />
              Generate with AI
            </button>
          )}
        </div>

        {/* Name */}
        <div className="mb-5">
          <p className="text-sm font-medium text-slate-700 mb-1.5">
            Contest name <span className="text-rose-500">*</span>
          </p>
          <TextField
            placeholder="Summer Sales Showdown"
            fullWidth
            size="small"
            value={form.name}
            onChange={(e) => handleChange({ name: e.target.value })}
            InputProps={{ sx: { ...textFieldSx, height: 45, padding: "5px" } }}
          />
        </div>

        {/* Description */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-1.5">Description</p>
          <TextField
            placeholder="What's the goal? What gets people fired up?"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={form.description}
            onChange={(e) => handleChange({ description: e.target.value })}
            InputProps={{ sx: textFieldSx }}
          />
        </div>

        {/* Contest type toggle. Disabled on edit because the backend rejects
            type changes — switching invalidates the participants set, the
            metric registry, and the leaderboard subject column. Easier to
            delete + re-create than to migrate in-place. */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-1.5">
            Contest type <span className="text-rose-500">*</span>
          </p>
          <ContestTypeToggle
            value={contestType}
            onChange={setContestType}
            disabled={isEdit}
          />
          {isEdit && (
            <p className="text-xs text-slate-400 mt-1">
              Contest type can't be changed after creation.
            </p>
          )}
        </div>

        {/* Locations — full width. Used to be side-by-side with
            Participants, but Locations needs the horizontal room
            (especially in multi-store contests) and the participants
            affordance reads more naturally as a follow-up below. */}
        {showMultiStorePicker && (
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              Locations <span className="text-rose-500">*</span>
              {selectedStoreIds.length > 1 && (
                <span className="text-slate-400 font-normal ml-1">
                  ({selectedStoreIds.length})
                </span>
              )}
            </p>
            <LocationsMultiSelect
              storeNames={locationsSource}
              selectedStoreIds={selectedStoreIds}
              onChange={setSelectedStoreIds}
            />
            {isStoreVsStore && selectedStoreIds.length < 2 && (
              <p className="text-xs text-amber-700 mt-1">
                Pick at least 2 locations to make a store-vs-store contest.
              </p>
            )}
          </div>
        )}

        {/* Participants — borderless inline checkbox. Default OFF (=
            open to everyone at the selected locations). Flipping ON
            reveals the picker. Framing around the *restriction* (not
            the inclusion) makes the default state self-explaining —
            "you don't need to do anything here" is signaled by the
            toggle being off. Copy uses "this/these location{s}"
            based on the selected-store count so it reads naturally. */}
        {!isStoreVsStore && (
          <div className="mb-6">
            {/* No "Participants" label — the toggle's own text
                ("Limit to specific employees at this/these
                location{s}") already explains the section, and a
                redundant header just adds noise. */}
            <label
              htmlFor="contest-limit-participants"
              className="flex items-start gap-3 cursor-pointer"
            >
              <input
                id="contest-limit-participants"
                type="checkbox"
                checked={limitToSpecific}
                onChange={(e) => {
                  setLimitToSpecific(e.target.checked);
                  if (!e.target.checked) setSelectedStmIds([]);
                }}
                className="w-4 h-4 mt-0.5 accent-violet-700"
              />
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">
                  Limit to specific employees at{" "}
                  {selectedStoreIds.length === 1 ? "this" : "these"} location
                  {selectedStoreIds.length === 1 ? "" : "s"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {limitToSpecific
                    ? "Only the selected employees will compete."
                    : "Currently, all employees are enrolled."}
                </div>
              </div>
            </label>
            {limitToSpecific && (
              <div className="mt-3">
                <EmployeesMultiSelect
                  employees={employees}
                  selectedStmIds={selectedStmIds}
                  onChange={setSelectedStmIds}
                  loading={employeesLoading}
                  showStoreTags={showStoreTags}
                />
                {participantSummary && (
                  <p className="text-xs text-slate-500 mt-1">
                    {participantSummary}
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Metric */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-1.5">
            Metric <span className="text-rose-500">*</span>
          </p>
          <MetricSelect
            value={ratioMode ? RATIO_OPTION_VALUE : form.metric_key}
            onChange={(v) => {
              if (v === RATIO_OPTION_VALUE) {
                setRatioMode(true);
                setRatioNum("");
                setRatioDen("");
                handleChange({ metric_key: "" });
              } else {
                setRatioMode(false);
                setRatioNum("");
                setRatioDen("");
                handleChange({ metric_key: v });
              }
            }}
            metrics={metricOptions}
          />
          {ratioMode && (
            <div className="mt-2 flex flex-col gap-2">
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">
                  Numerator (what they sell)
                </p>
                <MetricSelect
                  value={ratioNum}
                  onChange={(v) => handleRatioPick("num", v)}
                  metrics={ratioNumerators.filter(
                    (m) => m.value !== ratioDen
                  )}
                />
              </div>
              <div>
                <p className="text-xs font-medium text-slate-600 mb-1">
                  Denominator (per what)
                </p>
                <MetricSelect
                  value={ratioDen}
                  onChange={(v) => handleRatioPick("den", v)}
                  metrics={ratioDenominators.filter(
                    (m) => m.value !== ratioNum
                  )}
                />
              </div>
            </div>
          )}
          {selectedMetric && (
            <p className="text-xs text-slate-500 mt-2">
              {formatHint(selectedMetric)}
            </p>
          )}
          {ratioMode && !isStoreVsStore && (
            <p className="text-xs text-slate-500 mt-2">
              Ranks each employee on their rate, not their total. Employees
              with no denominator activity in the contest window aren't
              ranked. Consider setting a minimum shifts to rank so a
              low-volume outlier doesn't top the leaderboard.
            </p>
          )}
          {ratioMode && isStoreVsStore && (
            <p className="text-xs text-slate-500 mt-2">
              Each location races its own target, so different-volume stores
              compete fairly. Locations with no
              {" "}{ratioDenMeta?.label || "denominator"} activity in the
              contest window aren't ranked.
            </p>
          )}
          {selectedMetric?.direction === "lower_is_better" && !isStoreVsStore && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mt-2">
              Heads up: this metric rewards <strong>lower</strong> values. Consider
              setting a minimum-shifts-to-rank below so an employee with zero
              activity doesn't accidentally top the leaderboard.
            </p>
          )}
        </div>

        {/* Per-store targets. Shown only for metrics that rank each store
            against its own goal (e.g. Labor % vs Target). One row per selected
            location: the target (percent points) plus an optional GM name used
            as the store's label on the leaderboard / race. */}
        {requiresTargets && (
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              {isGoalMetric ? "Location goals" : "Location targets"}{" "}
              <span className="text-rose-500">*</span>
            </p>
            {isGoalMetric ? (
              <p className="text-xs text-slate-500 mb-2">
                Each location is ranked on how much of its own goal it reaches.
                Enter the total each location should hit by the end of the
                contest; the manager name is optional and labels the location on
                the leaderboard.
              </p>
            ) : (
              <p className="text-xs text-slate-500 mb-2">
                Each location is ranked on how far it beats its own target. Enter
                the target for every selected location; the manager name is
                optional and labels the location on the leaderboard.
              </p>
            )}
            {ratioMode && (
              <p className="text-xs text-slate-500 mb-2">
                Target is {ratioNumMeta?.label || "items"} per 100{" "}
                {ratioDenMeta?.label || "orders"}: for example 25 means 25{" "}
                {ratioNumMeta?.label || "items"} for every 100{" "}
                {ratioDenMeta?.label || "orders"}.
              </p>
            )}
            {selectedStoreIds.length === 0 ? (
              <p className="text-xs text-slate-400">
                Pick locations above to set their targets.
              </p>
            ) : (
              <div className="flex flex-col gap-2">
                {selectedStoreIds.map((sid) => (
                  <div key={sid} className="flex items-center gap-2">
                    <div className="w-40 shrink-0 text-sm text-slate-700 truncate">
                      {storeInfoLookup.get(String(sid)) || `Store ${sid}`}
                    </div>
                    <TextField
                      type="number"
                      size="small"
                      placeholder={isGoalMetric ? "Goal" : "Target %"}
                      value={storeTargets[sid]?.target_value ?? ""}
                      onChange={(e) =>
                        updateStoreTarget(sid, "target_value", e.target.value)
                      }
                      sx={{ width: 150 }}
                      // Focus/bg/radius go on InputProps (the OutlinedInput
                      // root) so the violet focus rule matches, like the other
                      // fields on this form.
                      InputProps={{ sx: textFieldSx }}
                      inputProps={{ min: 0, step: isGoalMetric ? 1 : 0.1 }}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="Manager (optional)"
                      value={storeTargets[sid]?.gm_label ?? ""}
                      onChange={(e) =>
                        updateStoreTarget(sid, "gm_label", e.target.value)
                      }
                      // Cap the width so it doesn't sprawl on wide layouts; the
                      // manager name is short.
                      sx={{ maxWidth: 340 }}
                      InputProps={{ sx: textFieldSx }}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dates */}
        <div className="mb-6 flex gap-4 flex-row md:flex-col">
          <DateField
            label="Start date *"
            value={form.start_date}
            onChange={(d) => handleChange({ start_date: d })}
          />
          <DateField
            label="End date *"
            value={form.end_date}
            onChange={(d) => handleChange({ end_date: d })}
            minDate={form.start_date}
          />
        </div>

        {/* Winners + min shifts. ``min_shifts_to_rank`` is a per-employee
            eligibility gate — it doesn't apply to store-vs-store contests,
            so the field is hidden in that mode (the backend also normalizes
            it to 0). */}
        {/* Winner rule — only for per-store-target metrics, where "everyone
            who hits their target" is a meaningful alternative to top-N. */}
        {requiresTargets && (
          <div className="mb-6">
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              Who wins?
            </p>
            <CardRadioGroup
              options={WINNER_MODE_OPTIONS}
              value={form.winner_mode}
              onChange={(v) => handleChange({ winner_mode: v })}
            />
          </div>
        )}
        {/* Hit-target mode is store-vs-store only, so every field in this
            row (winners count, per-employee min shifts, ratio min
            denominator) is moot there — drop the whole row. */}
        {!hitTargetMode && (
        <div className="mb-6 flex gap-4 flex-row md:flex-col">
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              Number of winners <span className="text-rose-500">*</span>
            </p>
            <TextField
              type="number"
              fullWidth
              size="small"
              value={form.winners_count}
              onChange={(e) => handleChange({ winners_count: e.target.value })}
              InputProps={{ sx: { ...textFieldSx, height: 45, padding: "5px" } }}
            />
            <p className="text-xs text-slate-500 mt-1">
              Top {form.winners_count || 0}{" "}
              {isStoreVsStore ? "locations" : "finishers"} earn rewards.
            </p>
          </div>
          {!isStoreVsStore && (
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-700 mb-1.5">
              Min shifts to rank
            </p>
            <TextField
              type="number"
              fullWidth
              size="small"
              value={form.min_shifts_to_rank}
              onChange={(e) =>
                handleChange({ min_shifts_to_rank: e.target.value })
              }
              InputProps={{ sx: { ...textFieldSx, height: 45, padding: "5px" } }}
            />
            <p className="text-xs text-slate-500 mt-1">
              0 to include everyone; otherwise filters out participants below
              this threshold.
            </p>
          </div>
          )}
          {/* Individual-ratio-only: minimum denominator volume before a rate
              ranks. Label follows the denominator pick (e.g. "Min Orders to
              rank"), so it reads naturally for any driver pair. Store ratio
              contests don't need it — store volumes are never tiny. */}
          {ratioMode && !isStoreVsStore && (
            <div className="flex-1">
              <p className="text-sm font-medium text-slate-700 mb-1.5">
                Min {ratioDenMeta?.label || "denominator"} to rank
              </p>
              <TextField
                type="number"
                fullWidth
                size="small"
                value={form.min_denominator_to_rank}
                onChange={(e) =>
                  handleChange({ min_denominator_to_rank: e.target.value })
                }
                InputProps={{ sx: { ...textFieldSx, height: 45, padding: "5px" } }}
              />
              <p className="text-xs text-slate-500 mt-1">
                A rate built on fewer {ratioDenMeta?.label || "denominator"}{" "}
                than this doesn't rank. Keeps tiny samples from topping the
                board.
              </p>
            </div>
          )}
        </div>
        )}

        {/* Reward description */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-1.5">
            Reward description
          </p>
          <TextField
            placeholder="What does the winner get? (1st: $200, 2nd: $100…)"
            fullWidth
            multiline
            rows={3}
            size="small"
            value={form.reward_description}
            onChange={(e) => handleChange({ reward_description: e.target.value })}
            InputProps={{ sx: textFieldSx }}
          />
        </div>

        {/* Visibility */}
        <div className="mb-6">
          <p className="text-sm font-medium text-slate-700 mb-1.5">
            Who can see this?
          </p>
          <VisibilityToggle
            value={form.visibility}
            onChange={(v) => handleChange({ visibility: v })}
          />
          {contest?.is_draft && (
            <p className="text-xs text-slate-500 mt-2">
              Applies once published. While in draft, only you and admins can
              see this contest.
            </p>
          )}
        </div>
      </div>

      <AiAssetGeneratorModal
        variant="contest"
        open={aiGenOpen}
        handleClose={() => setAiGenOpen(false)}
        onGenerated={applyAiDraft}
        // Prompt-only variant: the backend loads the metric catalog for this
        // store's company itself, scoped to the current contest type so the
        // pick always exists in the form's metric dropdown.
        requestParams={{ store_id: homeStore, contest_type: contestType }}
      />
    </div>
  );
}
